import type { Category, Config, Consent } from './types';
import { ALL_CATEGORIES, emptyConsent } from './storage';

import { CSS as styleCss } from './style.gen';

const ROOT_ID = 'cookiesie-root';
const STYLE_ID = 'cookiesie-style';

export interface BannerHandlers {
  onAcceptAll(): void;
  onRejectAll(): void;
  onSavePartial(c: Consent): void;
}

export function ensureStyle(): void {
  if (document.getElementById(STYLE_ID)) return;
  const s = document.createElement('style');
  s.id = STYLE_ID;
  s.textContent = styleCss;
  document.head.appendChild(s);
}

export function applyTheme(config: Config): void {
  const t = config.theme;
  const root = document.documentElement;
  root.style.setProperty('--cks-primary', t.primary);
  root.style.setProperty('--cks-text', t.text);
  root.style.setProperty('--cks-bg', t.background);
  root.style.setProperty('--cks-border', t.border);
}

export function removeBanner(): void {
  document.getElementById(ROOT_ID)?.remove();
}

export function renderBanner(
  config: Config,
  handlers: BannerHandlers,
  initialConsent: Consent | null,
  startInPrefs = false,
): void {
  removeBanner();
  ensureStyle();
  applyTheme(config);

  const root = document.createElement('div');
  root.id = ROOT_ID;
  root.className = `cks-root cks-${config.theme.position}`;
  root.setAttribute('role', 'region');
  root.setAttribute('aria-label', config.copy.title);
  root.setAttribute('lang', config.language);

  const card = document.createElement('div');
  card.className = 'cks-card';

  const title = document.createElement('p');
  title.className = 'cks-title';
  title.textContent = config.copy.title;

  const body = document.createElement('p');
  body.className = 'cks-body';
  body.textContent = config.copy.body;

  // Equal-prominence first-layer buttons. mvp_b_plan.md §6.3.
  // Order: Reject first so it's not visually a fallback choice.
  const actions = document.createElement('div');
  actions.className = 'cks-actions';

  const rejectBtn = makeBtn(config.copy.rejectAll, handlers.onRejectAll);
  const acceptBtn = makeBtn(config.copy.acceptAll, handlers.onAcceptAll);
  const manageBtn = document.createElement('button');
  manageBtn.type = 'button';
  manageBtn.className = 'cks-link';
  manageBtn.textContent = config.copy.managePrefs;

  actions.append(rejectBtn, acceptBtn, manageBtn);

  // Prefs panel — collapsed by default; no pre-ticked toggles for non-required.
  const prefs = document.createElement('div');
  prefs.className = 'cks-prefs';

  const prefsState: Consent = emptyConsent();
  if (initialConsent) {
    for (const k of ALL_CATEGORIES) prefsState[k] = !!initialConsent[k];
  }
  prefsState.strictly_necessary = true;

  for (const cat of config.categories) {
    prefs.appendChild(renderCategoryRow(cat, prefsState));
  }

  const saveBtn = makeBtn(config.copy.save, () =>
    handlers.onSavePartial({ ...prefsState }),
  );
  const saveRow = document.createElement('div');
  saveRow.className = 'cks-actions';
  saveRow.style.marginTop = '10px';
  saveRow.appendChild(saveBtn);
  prefs.appendChild(saveRow);

  manageBtn.addEventListener('click', () => {
    prefs.classList.toggle('cks-open');
    manageBtn.setAttribute(
      'aria-expanded',
      prefs.classList.contains('cks-open') ? 'true' : 'false',
    );
  });

  if (startInPrefs) {
    prefs.classList.add('cks-open');
    manageBtn.setAttribute('aria-expanded', 'true');
  }

  const footer = document.createElement('p');
  footer.className = 'cks-footer';
  footer.textContent = config.copy.poweredBy;

  card.append(title, body, actions, prefs, footer);
  root.appendChild(card);
  document.body.appendChild(root);
}

function makeBtn(label: string, onClick: () => void): HTMLButtonElement {
  const b = document.createElement('button');
  b.type = 'button';
  b.className = 'cks-btn';
  b.textContent = label;
  b.addEventListener('click', onClick);
  return b;
}

function renderCategoryRow(
  cat: { id: Category; label: string; description?: string; required?: boolean },
  state: Consent,
): HTMLElement {
  const row = document.createElement('div');
  row.className = 'cks-cat';

  const cb = document.createElement('input');
  cb.type = 'checkbox';
  cb.id = `cks-cat-${cat.id}`;
  cb.checked = !!state[cat.id];
  if (cat.required) {
    cb.checked = true;
    cb.disabled = true;
  } else {
    cb.addEventListener('change', () => {
      state[cat.id] = cb.checked;
    });
  }

  const text = document.createElement('div');
  text.className = 'cks-cat-text';
  const label = document.createElement('label');
  label.htmlFor = cb.id;
  label.textContent = cat.label;
  text.appendChild(label);
  if (cat.description) {
    const p = document.createElement('p');
    p.textContent = cat.description;
    text.appendChild(p);
  }

  row.append(cb, text);
  return row;
}
